import { createContext, useContext } from 'react';
import { createTheme, type Theme, type PaletteMode } from '@mui/material';

// ─── Warm Stone — admin MUI theme ──────────────────────────────────────────
// Neutral, brand-agnostic system shared with the mobile app: warm greige
// neutrals, INK near-black primary buttons, a quiet slate-blue accent, Inter
// type (no serif). Light + dark. See
// docs/superpowers/specs/2026-06-23-warm-stone-foundation-design.md

const INTER = '"Inter", "Helvetica Neue", Arial, system-ui, sans-serif';

interface StoneTokens {
  bg: string;
  paper: string;
  surface: string;
  textPrimary: string;
  textSecondary: string;
  divider: string;
  ink: string;
  inkContrast: string;
  inkHover: string;
  accent: string;
  accentDeep: string;
  accentContrast: string;
  accentSelected: string;
  accentSelectedHover: string;
  error: string;
  success: string;
  warning: string;
  cardShadow: string;
}

const LIGHT: StoneTokens = {
  bg: '#FAF9F6',
  paper: '#FFFFFF',
  surface: '#F2F0EA',
  textPrimary: '#1C1B19',
  textSecondary: '#6E6A62',
  divider: '#DAD6CC',
  ink: '#1C1B19',
  inkContrast: '#FFFFFF',
  inkHover: '#2A2824',
  accent: '#3C5A75',
  accentDeep: '#2E4860',
  accentContrast: '#FFFFFF',
  accentSelected: 'rgba(60,90,117,0.10)',
  accentSelectedHover: 'rgba(60,90,117,0.16)',
  error: '#B23B33',
  success: '#2F6B4F',
  warning: '#9A6B1E',
  cardShadow: '0 1px 2px rgba(28,27,25,0.04), 0 6px 16px rgba(28,27,25,0.05)',
};

const DARK: StoneTokens = {
  bg: '#181714',
  paper: '#211F1C',
  surface: '#2A2824',
  textPrimary: '#F2F0EA',
  textSecondary: '#B7B1A6',
  divider: '#35322D',
  ink: '#EDEAE2',
  inkContrast: '#1C1B19',
  inkHover: '#FFFFFF',
  accent: '#88A8C6',
  accentDeep: '#A6BFD6',
  accentContrast: '#12222E',
  accentSelected: 'rgba(136,168,198,0.16)',
  accentSelectedHover: 'rgba(136,168,198,0.24)',
  error: '#E08A82',
  success: '#6FB58F',
  warning: '#D6A84E',
  cardShadow: 'none',
};

export function buildTheme(mode: PaletteMode): Theme {
  const t = mode === 'dark' ? DARK : LIGHT;
  return createTheme({
    palette: {
      mode,
      primary: { main: t.ink, dark: t.inkHover, contrastText: t.inkContrast },
      secondary: { main: t.accent, dark: t.accentDeep, contrastText: t.accentContrast },
      background: { default: t.bg, paper: t.paper },
      text: { primary: t.textPrimary, secondary: t.textSecondary },
      divider: t.divider,
      error: { main: t.error },
      success: { main: t.success },
      warning: { main: t.warning },
      info: { main: t.accent },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: INTER,
      h1: { fontFamily: INTER, fontWeight: 600, letterSpacing: '-0.4px' },
      h2: { fontFamily: INTER, fontWeight: 600, letterSpacing: '-0.3px' },
      h3: { fontFamily: INTER, fontWeight: 600, letterSpacing: '-0.2px' },
      h4: { fontFamily: INTER, fontWeight: 600 },
      h5: { fontFamily: INTER, fontWeight: 600 },
      h6: { fontFamily: INTER, fontWeight: 600 },
      button: { fontWeight: 500 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: { html: { scrollBehavior: 'smooth' } },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            transition: 'background-color 180ms ease, box-shadow 180ms ease, transform 120ms ease',
            '&:active': { transform: 'scale(0.98)' },
            '&.Mui-focusVisible': { outline: `2px solid ${t.accent}`, outlineOffset: 2 },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: t.accentSelected,
              boxShadow: `inset 3px 0 0 ${t.accent}`,
              '&:hover': { backgroundColor: t.accentSelectedHover },
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          outlined: {
            borderColor: t.divider,
            boxShadow: t.cardShadow,
          },
        },
      },
      MuiCard: {
        styleOverrides: { root: { borderRadius: 14 } },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: t.surface,
            color: t.textSecondary,
            fontWeight: 600,
            fontSize: 12,
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            borderBottomColor: t.divider,
          },
          root: { borderBottomColor: t.divider },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 8 },
          notchedOutline: { borderColor: t.divider },
        },
      },
      MuiChip: {
        styleOverrides: { root: { borderRadius: 999, fontWeight: 500 } },
      },
    },
  });
}

// ─── Color-mode context (light/dark toggle) ────────────────────────────────
export interface ColorModeCtx {
  mode: PaletteMode;
  toggle: () => void;
}

export const ColorModeContext = createContext<ColorModeCtx>({
  mode: 'light',
  toggle: () => {},
});

export const useColorMode = () => useContext(ColorModeContext);

const STORAGE_KEY = 'livinhome:color-mode';

/** Initial mode: stored preference, else the OS preference, else light. */
export function initialColorMode(): PaletteMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function persistColorMode(mode: PaletteMode): void {
  localStorage.setItem(STORAGE_KEY, mode);
}
